'use client';

import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import type { MorningBriefingBlock, MorningTile } from '@/lib/chat/blocks/morning-briefing';

/**
 * The morning briefing inside Mucka's reply to "morning": up to four tiles,
 * the first job up, and up to three follow-up questions. Board:
 * `project/Morning.dc.html`.
 *
 * 🚨 Nothing here computes a figure. Every number is off the block, built on
 * the server from the REDACTED business context (`morning-briefing.ts`), and a
 * tile the seat may not see is simply not in the block — this component never
 * decides what someone may be told.
 *
 * Taps: a tile or the first job opens a page (a destination the server has
 * already checked opens for this seat; one that does not is drawn without a
 * tap). A follow-up asks Mucka in the owner's own words. Nothing sends or
 * changes anything, so nothing is orange. Follow-ups are off when `live` is
 * false — on an older reply the question is about a day that has moved on —
 * while the tiles still open their pages, as the owed table's rows do.
 */
export interface MorningBriefingProps {
  block: MorningBriefingBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}

function pounds(n: number): string {
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Label, big figure and small print for one tile. Wording only; no arithmetic. */
export function tileText(tile: MorningTile): { label: string; value: string; detail: string | null } {
  switch (tile.kind) {
    case 'jobs': {
      let detail: string | null;
      if (tile.count === 0) detail = 'Nothing booked in';
      else if (tile.wholeTeam) {
        detail = tile.split.map(s => `${s.who} ${s.count}`).join(' · ') + (tile.splitMore ? ` · +${tile.splitMore} more` : '');
      } else detail = tile.done ? `${tile.done} done` : null;
      return { label: tile.wholeTeam ? 'Jobs today' : 'Your jobs today', value: String(tile.count), detail };
    }
    case 'paid':
      return { label: 'Paid overnight', value: pounds(tile.amount), detail: `${plural(tile.invoices, 'invoice')} since 6pm` };
    case 'overdue':
      return {
        label: 'Overdue',
        value: pounds(tile.amount),
        detail: `${plural(tile.invoices, 'invoice')}, oldest ${plural(tile.oldestDays, 'day')}`,
      };
    case 'waiting':
      return { label: 'Waiting on you', value: String(tile.count), detail: tile.count === 1 ? 'Card to decide' : 'Cards to decide' };
    case 'parts': {
      const where = tile.merchants.length ? `From ${tile.merchants.join(', ')}` : null;
      const notIn = tile.notIn ? `${tile.notIn} not in yet` : null;
      return {
        label: 'Parts to pick up',
        value: String(tile.toCollect),
        detail: [where, notIn].filter(Boolean).join(' · ') || null,
      };
    }
  }
}

function Tile({ tile, onOpen }: { tile: MorningTile; onOpen: (path: string) => void }) {
  const { label, value, detail } = tileText(tile);
  const body = (
    <>
      <span className="mchat-mb-tile-label">{label}</span>
      <span className="mchat-mb-tile-value">{value}</span>
      {detail && <span className="mchat-mb-tile-detail">{detail}</span>}
    </>
  );
  const cls = `mchat-mb-tile mchat-mb-tile--${tile.kind}`;
  if (!tile.path) return <div className={cls} role="listitem">{body}</div>;
  const path = tile.path;
  return (
    <button
      type="button"
      role="listitem"
      className={cls}
      aria-label={`${label}: ${value}${detail ? `, ${detail}` : ''}`}
      onClick={() => onOpen(path)}
    >
      {body}
    </button>
  );
}

export function MorningBriefing({ block, live, onAnswer, onOpen }: MorningBriefingProps) {
  const [asked, setAsked] = useState<string | null>(null);
  return (
    <div className="mchat-mb" role="group" aria-label="Morning briefing">
      <p className="mchat-mb-asof">As of {block.asOf}</p>
      {block.tiles.length > 0 && (
        <div className="mchat-mb-tiles" role="list" aria-label={`${block.tiles.length} figures for today`}>
          {block.tiles.map(tile => <Tile key={tile.kind} tile={tile} onOpen={onOpen} />)}
        </div>
      )}
      {block.firstUp && (
        <>
          <div className="mchat-group mchat-mb-label" role="presentation">First up</div>
          <button
            type="button"
            className="mchat-row mchat-mb-first"
            aria-label={`First up at ${block.firstUp.time}: ${block.firstUp.title}${block.firstUp.detail ? `, ${block.firstUp.detail}` : ''}`}
            onClick={() => onOpen(block.firstUp!.path)}
          >
            <span className="mchat-mb-time">{block.firstUp.time}</span>
            <span className="mchat-row-main">
              <span className="mchat-row-title">{block.firstUp.title}</span>
              {block.firstUp.detail && <span className="mchat-row-detail">{block.firstUp.detail}</span>}
            </span>
            <Icon icon={ChevronRight} size={15} className="mchat-row-go" />
          </button>
        </>
      )}
      {block.followUps.length > 0 && (
        <div className="mchat-mb-follow">
          {block.followUps.map(text => (
            <button
              key={text}
              type="button"
              className={`mchat-mb-chip${asked === text ? ' mchat-mb-chip--asked' : ''}`}
              disabled={!live || asked !== null}
              onClick={() => { setAsked(text); onAnswer(text); }}
            >
              {text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
